import classNames from "classnames";
import { ReactNode, useContext } from "react";
import { DefaultValues, FieldValues, Mode, UseFormReset, UseFormReturn, useForm } from "react-hook-form";
import ConfigurationSaveStatusDispatch from "../../context/configurationSaveStatusContext";
import Button from "./Button";
import styles from "./Form.module.css";
import LoadingSpinner from "./LoadingSpinner";
import SubmitBar from "./SubmitBar";

export type FormChildrenProps<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  disabled?: boolean;
};

type Props<TFieldValues extends FieldValues> = {
  disabled?: boolean;
  secondaryButton?: ReactNode;
  onSubmit?: (reset: UseFormReset<TFieldValues>) => (data: TFieldValues) => Promise<void>;
  submitText?: string;
  defaultValues?: DefaultValues<TFieldValues>;
  children?: ReactNode | ((useFormProps: FormChildrenProps<TFieldValues>) => ReactNode);
  mode?: Mode;
  fullWidth?: boolean;
};

const Form = <TFieldValues extends FieldValues>({
  children,
  disabled,
  secondaryButton,
  submitText,
  defaultValues,
  onSubmit,
  mode = "onSubmit",
  fullWidth = false,
}: Props<TFieldValues>) => {
  const useFormProps = useForm<TFieldValues>({
    defaultValues,
    mode: mode,
  });

  const isDisabled = disabled || useFormProps.formState.disabled;

  const setSaveStatus = useContext(ConfigurationSaveStatusDispatch);

  // wraps RHF's handleSubmit so any submit attempt that fails validation is reflected
  // in the configuration save status, regardless of whether it's triggered via the
  // form's own submit button or a field-level autosave call further down the tree.
  const handleSubmit: UseFormReturn<TFieldValues>["handleSubmit"] = (onValid, onInvalid) =>
    useFormProps.handleSubmit(onValid, (errors, event) => {
      setSaveStatus("draft");
      return onInvalid?.(errors, event);
    });

  return (
    <form
      className={classNames(styles.form, fullWidth ? styles.fullWidth : undefined)}
      onSubmit={(e) => {
        if (onSubmit) {
          e.stopPropagation();
          handleSubmit(onSubmit(useFormProps.reset))(e);
        }
      }}
    >
      {typeof children === "function" ? children({ ...useFormProps, handleSubmit, disabled: isDisabled }) : children}

      {((onSubmit && submitText) || secondaryButton) && (
        <SubmitBar>
          {!!useFormProps.formState.isSubmitting && <LoadingSpinner />}
          {!!secondaryButton && secondaryButton}
          {onSubmit && submitText && (
            <Button
              buttonProps={{
                type: "submit",
                disabled: isDisabled || useFormProps.formState.isSubmitting,
              }}
            >
              {submitText}
            </Button>
          )}
        </SubmitBar>
      )}
    </form>
  );
};

export default Form;
